/**
 * simple-filter-node.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Component, Input } from "@angular/core";
import { ServerDataType } from "../common/data/data-types";
import { TypedData } from "../common/data/typed-data";
import {
    ConstantNode,
    FilterNode,
    OperatorNode,
    UndefinedNode,
    VariableNode,
		ArgumentNode
} from "./filter-node";
import {
    FilterNodeType,
    INEQUALITIES,
    Operator,
    Operators,
    OperatorSignature
} from "./operators";
import { StdVariable } from "./typed-parameter";

@Component({
    selector: "simple-filter-node",
    template: `
<div>
<template [ngIf]="isValidRoot(node)">
    <!-- TODO: selector for alternative values -->
    <p class="intro" *ngIf="!skipText">
        In <em>simple</em> mode, the filter is constructed out of a number of
        "subfilters" limited to the format <code>variable operator value</code>.
        All of the subfilters must be true for the record to be a match (as if
        each expression were linked with an <kbd>and</kbd>). For more
        complicated expressions such as <kbd>or</kbd> conditions and arithmetic,
        you must use <em>advanced</em> mode.
    </p>
    <table>
        <thead>
            <th></th>
            <th>Variable</th>
            <th>Operator</th>
            <th>Value</th>
        </thead>
        <tbody>
            <tr *ngFor="let clause of node.operands; let i = index">
								<td>{{i + 1}}</td>
                <template [ngIf]="isValidClause(clause)">
                    <td *ngIf="clause.left.nodeType == 'variable'">
                        <select
                            [formControl]="clause.left.variableControl"
                            class="form-control">
                            <option disabled [ngValue]="null">
                                -- Select variable --
                            </option>
                            <option
                                *ngFor="let sv of clause.left.filteredVariables"
                                [value]="sv.name">
                                {{sv.displayName || sv.name}}
                            </option>
                        </select>
										</td>
										<td *ngIf="clause.left.nodeType == 'argument'">
											<em>
												(item of 
												<kbd>{{this.forCollection.displayName}}</kbd>
												list)
											</em>
											<!--<button
												class="btn btn-default btn-xs"
												(click)="revertToVariable(clause)">
												Revert to variable
											</button>-->
										</td>
                    <td>
                        <select
                            [formControl]="clause.operatorControl"
                            class="form-control operatorControl">
                            <option disabled [ngValue]="null">
                                -- Select operator --
                            </option>
                            <option
                                *ngFor="let op of clause.availableOperators"
                                [value]="op.name"
                                [disabled]="!op.userSelectable">
                                {{op.name}}
                            </option>
                        </select>
                    </td>
                    <td>
                        <typed-control
                            *ngIf="clause.right && clause.right.type"
                            [input]="clause.right"
                            [control]="clause.right.valueControl"
                            class="constantControl"
                            [iconTray]="false"
                            [clearButton]="false"
                            [validationTray]="false">
                        </typed-control>
                        <input
                            *ngIf="clause.right && !clause.right.type"
                            type="text"
                            class="form-control"
                            disabled>
                        <em *ngIf="!clause.right">N/A</em>
                    </td>
                </template>
                <td colspan="3" *ngIf="!isValidClause(clause)">
                    <p class="text-danger">
                        (this node cannot be used with the simple editor)
                    </p>
                    <p [innerHTML]="readableFor(clause)"></p>
                </td>
            </tr>
            <tr>
                <td>{{node.operands.length + 1}}</td>
                <td colspan="3">
                    <button
                        class="btn btn-default p100"
                        (click)="addClause()">
                        Add Sub-filter
                    </button>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<div *ngIf="!isValidRoot(node)" class="text-danger">
    <p class="text-danger">(this node cannot be used with the simple editor)</p>
    <p [innerHTML]="readableFor(node)"></p>
</div>

</div>
    `,
    styles: [`
        .intro {margin-top: 10px;}
        table {
            width: 100%;
        }
        table td {
            /* border: 1px solid #ccc; */
            padding: 2px 5px;
        }
        .constant {
            margin-top: 13px;
        }
        table.constant td {
            padding: 0px;
        }
        .operatorControl {
            font-style: italic;
            color: var(--color-green)
        }
        .constantControl >>> .form-group {
            margin-bottom: 0px;
        }
        .revert {
            width: 100%;
        }
    `]
})
export class SimpleFilterNodeComponent
{
    @Input()
    public node : FilterNode;

    @Input()
		public variables : TypedData[];
		
		@Input()
		public skipText : boolean = false;

		@Input()
		public forCollection : StdVariable;

    private readablesCache : WeakMap<FilterNode, string> = new WeakMap();
    public readableFor (node : FilterNode) : string
    {
        if (!this.readablesCache.has(node))
        {
            this.readablesCache.set(node, node.toReadable());
        }
        return this.readablesCache.get(node);
    }

    protected readonly validRootOperators : Set<Operator> = new Set([
        Operators.get("all of")
    ]);

    public isValidRoot (node : FilterNode) : node is OperatorNode
    {
        return (node.nodeType === "expression"
            && this.validRootOperators.has((node as OperatorNode).operator));
    }

    public isValidClause (node : OperatorNode) : boolean
    {
        if ((node.nodeType === "expression" && node.operator)
						&& (node.left && 
							(node.left.nodeType === "variable" 
							|| (this.forCollection && node.left.nodeType === "argument"))))
        {
            if (node.operator.isBinary() && node.right)
            {
                if (node.right.nodeType !== "value")
                {
                    node.addChild(1, new ConstantNode());
                }
                return true;
            }
            return node.operator.isUnary();
        }
    }

    public addClause () : void
    {
        if (!this.isValidRoot(this.node))
        {
            throw new Error(
                "Cannot add clause when root is not simple filter!");
				}
				if (this.forCollection)
				{
					const eqNode = OperatorNode.BasicEquality(
						this.node.allowedVariables, false);
					eqNode.addChild(0, new ArgumentNode(this.forCollection))
					this.node.addChild(
            this.node.operands.length,
						eqNode);
				}
				else
				{
					this.node.addChild(
            this.node.operands.length,
            OperatorNode.BasicEquality(this.node.allowedVariables, false));
				}
		}
		
		public revertToVariable (clause : OperatorNode) : void
		{
			clause.addChild(0, new VariableNode());
		}

    public opNames () : string[]
    {
        return (this.node as OperatorNode).availableOperators.map(
            (op : Operator) => op.name);
    }

    public af<T> (ob : Set<T>) : T[]
    {
        if (!ob)
        {
            return null;
        }
        else
        {
            return Array.from(ob);
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/simple-filter-node.component.ts